'use client';
import React, { useState } from 'react';
import { Mail, ArrowRight, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import { createClient } from '@/lib/supabase/client';

export default function NewsletterBand() {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !email.includes('@')) {
      toast.error('Please enter a valid email address.');
      return;
    }

    setLoading(true);
    try {
      const supabase = createClient();

      // Save subscriber to Supabase
      const { error: dbError } = await supabase
        .from('newsletter_subscribers')
        .insert({ email: email.toLowerCase().trim() });

      if (dbError) {
        if (dbError.code === '23505') {
          // Unique violation — already subscribed
          toast.success("You're already subscribed! Thank you for your support.");
          setSubmitted(true);
          return;
        }
        throw dbError;
      }

      // Send welcome email via Resend edge function
      try {
        await supabase.functions.invoke('send-email', {
          body: {
            subject: 'Welcome to the MOURNDIECE Foundation Community!',
            body: `Thank you for subscribing to the MOURNDIECE Foundation newsletter!\n\nYou will now receive campaign updates, impact stories, and exclusive member news every two weeks.\n\nTogether, we are building a better world.\n\nWith gratitude,\nThe MOURNDIECE Foundation Team`,
            recipients: [{ email: email.toLowerCase().trim(), name: '' }],
            emailType: 'newsletter',
          },
        });
      } catch {
        // Welcome email failure is non-blocking — subscription is still saved
      }

      setSubmitted(true);
      toast.success("You're subscribed! Welcome to the MOURNDIECE community.");
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Subscription failed. Please try again.';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="py-16 lg:py-20 gradient-primary">
      <div className="max-w-screen-2xl mx-auto px-6 lg:px-8 xl:px-10">
        <div className="max-w-2xl mx-auto text-center">
          <div className="w-14 h-14 bg-white/15 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <Mail size={24} className="text-white" />
          </div>
          <h2 className="text-2xl lg:text-3xl font-bold text-white mb-3">
            Stay Close to the Cause
          </h2>
          <p className="text-blue-100 text-base mb-8 leading-relaxed">
            Join our growing community of supporters receiving campaign updates, impact stories, and exclusive member news every two weeks. No spam — ever.
          </p>

          {submitted ? (
            <div className="flex items-center justify-center gap-3 bg-white/15 rounded-xl px-8 py-4">
              <CheckCircle size={20} className="text-green-300" />
              <span className="text-white font-semibold">You're subscribed — check your inbox!</span>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                disabled={loading}
                className="flex-1 px-5 py-3.5 rounded-xl bg-white/15 border border-white/30 text-white placeholder-blue-200 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-white/50 focus:bg-white/20 transition-all disabled:opacity-60"
              />
              <button
                type="submit"
                disabled={loading}
                className="flex items-center justify-center gap-2 bg-white text-primary font-bold text-sm px-6 py-3.5 rounded-xl hover:bg-blue-50 transition-all duration-150 active:scale-95 whitespace-nowrap disabled:opacity-60"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <span className="w-4 h-4 border-2 border-primary/40 border-t-primary rounded-full animate-spin" />
                    Subscribing...
                  </span>
                ) : (
                  <>
                    Subscribe
                    <ArrowRight size={15} />
                  </>
                )}
              </button>
            </form>
          )}

          <p className="text-blue-200/70 text-xs mt-5">
            By subscribing, you agree to our Privacy Policy. Unsubscribe at any time.
          </p>
        </div>
      </div>
    </section>
  );
}